/**
 * Judge.me PDP Enhancement & Interaction Bridge
 * Ensures all interactive elements within the Judge.me review block
 * (Write a Review, Star/Histogram Filters, Sorting, Pagination, Helpful Votes)
 * are immediately responsive, preloaded, and resilient against timeout failures.
 */
(() => {
  if (window.__kpJudgemeFixInitialized) return;
  window.__kpJudgemeFixInitialized = true;

  const JM_CDN = 'https://cdnwidget.judge.me/';
  const JM_EXT_CDN = 'https://cdn.shopify.com/extensions/01a090ba-56bd-7925-a3b8-a815d35f8f1c/judgeme-752/assets/';

  const loadScript = (src, isModule = false) => {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      if (isModule) script.type = 'module';
      script.async = true;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  };

  const ensureCoreScripts = async () => {
    try {
      if (!window.jdgm) window.jdgm = {};
      if (!window.jdgm.$ || typeof window.jdgm.$ !== 'function') {
        await loadScript(JM_CDN + 'widget/base.js');
        await loadScript(JM_CDN + 'widget/common.js');
      }
      if (!window.jdgm._WriteReviewModal) {
        await Promise.all([
          loadScript(JM_CDN + 'widget/write_review_modal.js'),
          loadScript(JM_CDN + 'widget/form.js')
        ]);
      }
      return true;
    } catch (err) {
      console.warn('[KP Judge.me Fix] Failed to load core scripts:', err);
      return false;
    }
  };

  // Pre-warm scripts in the background
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ensureCoreScripts(), { once: true });
  } else {
    ensureCoreScripts();
  }

  // Fallback modal opener that never fails
  const openWriteReviewModal = async (productId, triggerBtn) => {
    if (triggerBtn) {
      triggerBtn.classList.add('kp-jm-loading');
      triggerBtn.dataset.kpLoading = 'true';
      if (!triggerBtn.querySelector('.kp-jm-spinner')) {
        const spinner = document.createElement('span');
        spinner.className = 'kp-jm-spinner';
        spinner.textContent = ' ...';
        triggerBtn.appendChild(spinner);
      }
    }

    try {
      await ensureCoreScripts();

      const modalHost = document.querySelector('#judgeme_product_reviews') || document.querySelector('.jdgm-review-widget');
      const pId = productId || modalHost?.dataset?.productId || modalHost?.dataset?.id;

      // Remove existing stale modals
      document.querySelectorAll('.jdgm-review-widget-modal.jdgm-write-review-modal').forEach((el) => el.remove());

      if (window.jdgm && window.jdgm._WriteReviewModal && window.jdgm.$) {
        const modal = new window.jdgm._WriteReviewModal(window.jdgm.$);
        const setupOk = await modal.setup('jdgm-review-widget-modal', pId, {});
        if (setupOk) {
          modal.showModalPage();
          return true;
        }
      }

      // Legacy fallback
      if (window.jdgm && typeof window.jdgm.openForm === 'function' && modalHost) {
        window.jdgm.openForm(window.jdgm.$(modalHost));
        return true;
      }
    } catch (err) {
      console.error('[KP Judge.me Fix] Error opening write review modal:', err);
    } finally {
      if (triggerBtn) {
        triggerBtn.classList.remove('kp-jm-loading');
        delete triggerBtn.dataset.kpLoading;
        const spinner = triggerBtn.querySelector('.kp-jm-spinner');
        if (spinner) spinner.remove();
      }
    }
    return false;
  };

  // Delegated Click Handler for ALL Judge.me buttons & controls
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // A. "Write a Review" button click handling
    const writeBtn = target.closest(
      '[data-testid="write-review-button"], .jm-action-buttons__button, .jdgm-write-rev-link, [data-action="write-review"], .jdgm-rev-widg__summary-actions .jdgm-write-rev-link'
    ) || (target.tagName === 'BUTTON' && target.textContent.trim().toLowerCase() === 'write a review' ? target : null);

    if (writeBtn) {
      if (writeBtn.dataset.kpLoading === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const widgetContainer = writeBtn.closest('#judgeme_product_reviews, .jdgm-widget, .jdgm-review-widget');
      const productId = widgetContainer?.dataset?.productId || widgetContainer?.dataset?.id || '7622941802635';

      event.preventDefault();
      event.stopPropagation();
      openWriteReviewModal(productId, writeBtn);
      return;
    }

    // B. Other controls inside Judge.me widget
    const jmWidget = target.closest('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-widget');
    if (!jmWidget) return;

    const isRevampReady = jmWidget.classList.contains('jdgm-widget-revamp') &&
      jmWidget.querySelector('.jdgm-review-widget-container');

    // C. Histogram / Star Rating Filter buttons
    const starFilter = target.closest('.jdgm-histogram__row, .jdgm-histogram__star, [data-rating]');
    if (starFilter && !isRevampReady) {
      event.preventDefault();
      starFilter.style.opacity = '0.6';
      loadScript(JM_EXT_CDN + 'review_widget.js', true).finally(() => {
        starFilter.style.opacity = '1';
        setTimeout(() => {
          const rating = starFilter.dataset.rating || starFilter.getAttribute('data-rating');
          const mountedRow = jmWidget.querySelector(`.jdgm-histogram__row[data-rating="${rating}"], [data-rating="${rating}"]`);
          if (mountedRow && mountedRow !== starFilter) {
            mountedRow.click();
          }
        }, 200);
      });
      return;
    }

    // D. Pagination buttons
    const pageBtn = target.closest('.jdgm-paginate__page, .jdgm-paginate__next-page, .jdgm-paginate__prev-page, [data-page]');
    if (pageBtn && !isRevampReady) {
      event.preventDefault();
      pageBtn.style.opacity = '0.6';
      loadScript(JM_EXT_CDN + 'review_widget.js', true).finally(() => {
        pageBtn.style.opacity = '1';
        setTimeout(() => {
          const page = pageBtn.dataset.page || pageBtn.getAttribute('data-page') || pageBtn.textContent.trim();
          const mountedPage = Array.from(jmWidget.querySelectorAll('.jdgm-paginate__page, [data-page]'))
            .find((el) => el.textContent.trim() === page || el.dataset.page === page);
          if (mountedPage && mountedPage !== pageBtn) {
            mountedPage.click();
          }
        }, 200);
      });
      return;
    }

    // E. Sort dropdown
    const sortDropdown = target.closest('.jdgm-sort-dropdown, [data-sort-method]');
    if (sortDropdown && !isRevampReady) {
      event.preventDefault();
      loadScript(JM_EXT_CDN + 'review_widget.js', true);
      return;
    }

    // F. Helpful / Vote buttons
    const voteBtn = target.closest('.jdgm-rev__thumb-up, .jdgm-rev__thumb-down, [data-action="vote"]');
    if (voteBtn && !isRevampReady) {
      event.preventDefault();
      voteBtn.style.opacity = '0.6';
      loadScript(JM_EXT_CDN + 'review_widget.js', true).finally(() => {
        voteBtn.style.opacity = '1';
      });
      return;
    }
  }, true);

  // Fallback for legacy widget initialization
  const checkLegacyWidget = () => {
    const legacyWidget = document.querySelector('.jdgm-legacy-widget-content:not([style*="display: none"]), .jdgm-rev-widg');
    if (legacyWidget && window.jdgm && typeof window.jdgm.initializeWidgets === 'function') {
      window.jdgm.initializeWidgets(window.jdgm.$ ? window.jdgm.$(legacyWidget) : undefined);
    }
  };

  // Unblock any pointer-events: none caused by AOS [data-aos] on the widget
  const unblockPointerEvents = () => {
    document.querySelectorAll('[data-aos]').forEach((el) => {
      if (el.querySelector('#judgeme_product_reviews') || el.id === 'judgeme_product_reviews') {
        el.removeAttribute('data-aos');
        el.style.pointerEvents = 'auto';
      }
    });
    const jm = document.querySelector('#judgeme_product_reviews');
    if (jm) jm.style.pointerEvents = 'auto';
  };

  unblockPointerEvents();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      checkLegacyWidget();
      unblockPointerEvents();
    });
  } else {
    checkLegacyWidget();
    unblockPointerEvents();
  }
})();
